import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import './i18n';
import './App.css';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import ClientLayout from './layouts/ClientLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import ClientsPage from './pages/admin/ClientsPage';
import SuppliersPage from './pages/admin/SuppliersPage';
import BrandsPage from './pages/admin/BrandsPage';
import ModelsPage from './pages/admin/ModelsPage';
import VehiclesPage from './pages/admin/VehiclesPage';
import ShippingPage from './pages/admin/ShippingPage';
import OrdersPage from './pages/admin/OrdersPage';
import OrderDetailPage from './pages/admin/OrderDetailPage';
import EmployeesPage from './pages/admin/EmployeesPage';
import TelegramSettingsPage from './pages/admin/TelegramSettingsPage';
import IntegrationsPage from './pages/admin/IntegrationsPage';
import ReportsPage from './pages/admin/ReportsPage';
import AutomationsPage from './pages/admin/AutomationsPage';
import MessagesPage from './pages/admin/MessagesPage';
import OrderDraftsPage from './pages/admin/OrderDraftsPage';
import ContainersPage from './pages/admin/ContainersPage';
import PortsPage from './pages/admin/PortsPage';
import LoadingGroupsPage from './pages/admin/LoadingGroupsPage';
import DouanePosPage from './pages/admin/DouanePosPage';
import HelpCenterPage from './pages/admin/HelpCenterPage';
import DealersPage from './pages/admin/DealersPage';
import CompaniesPage from './pages/admin/CompaniesPage';

// Public Pages
import PublicTrackingPage from './pages/public/PublicTrackingPage';
import PublicUploadPage from './pages/public/PublicUploadPage';
import PublicCatalogPage from './pages/public/PublicCatalogPage';

// Client Portal Pages
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalProfile from './pages/portal/PortalProfile';
import PortalOrders from './pages/portal/PortalOrders';
import PortalOrderDetail from './pages/portal/PortalOrderDetail';
import PortalMessages from './pages/portal/PortalMessages';
import PortalNewOrder from './pages/portal/PortalNewOrder';
import PortalEndClients from './pages/portal/PortalEndClients';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'client') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Block restricted sales agents (with see_own_orders_only) from admin-only pages.
const RestrictedGuard = ({ children }) => {
  const { user } = useAuth();
  const restricted = !['admin', 'master_admin'].includes(user?.role) && user?.permissions?.see_own_orders_only;
  if (restricted) return <Navigate to="/orders" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  const restricted = !['admin', 'master_admin'].includes(user?.role) && user?.permissions?.see_own_orders_only;
  return <Navigate to={restricted ? '/orders' : '/dashboard'} replace />;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'client') {
      return <Navigate to="/portal" replace />;
    }
    const restricted = !['admin', 'master_admin'].includes(user.role) && user.permissions?.see_own_orders_only;
    return <Navigate to={restricted ? '/orders' : '/dashboard'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/track" element={<PublicTrackingPage />} />
      <Route path="/u/:token" element={<PublicUploadPage />} />
      <Route path="/catalog/:slug" element={<PublicCatalogPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

      {/* Admin/Staff Routes */}
      <Route path="/" element={
        <ProtectedRoute allowedRoles={['admin', 'sales_agent', 'logistics_manager', 'operations', 'master_admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<RestrictedGuard><DashboardPage /></RestrictedGuard>} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="dealers" element={
          <ProtectedRoute allowedRoles={['master_admin']}><DealersPage /></ProtectedRoute>
        } />
        <Route path="companies" element={
          <ProtectedRoute allowedRoles={['master_admin']}><CompaniesPage /></ProtectedRoute>
        } />
        <Route path="suppliers" element={<RestrictedGuard><SuppliersPage /></RestrictedGuard>} />
        <Route path="brands" element={<BrandsPage />} />
        <Route path="models" element={<ModelsPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="shipping" element={<RestrictedGuard><ShippingPage /></RestrictedGuard>} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="employees" element={<RestrictedGuard><EmployeesPage /></RestrictedGuard>} />
        <Route path="telegram" element={<RestrictedGuard><TelegramSettingsPage /></RestrictedGuard>} />
        <Route path="integrations" element={<RestrictedGuard><IntegrationsPage /></RestrictedGuard>} />
        <Route path="reports" element={<RestrictedGuard><ReportsPage /></RestrictedGuard>} />
        <Route path="automations" element={<RestrictedGuard><AutomationsPage /></RestrictedGuard>} />
        <Route path="messages" element={<RestrictedGuard><MessagesPage /></RestrictedGuard>} />
        <Route path="order-drafts" element={<RestrictedGuard><OrderDraftsPage /></RestrictedGuard>} />
        <Route path="containers" element={<ContainersPage />} />
        <Route path="ports" element={<PortsPage />} />
        <Route path="loading-groups" element={<LoadingGroupsPage />} />
        <Route path="douane-pos" element={<DouanePosPage />} />
        <Route path="help" element={<HelpCenterPage />} />
      </Route>

      {/* Client Portal Routes */}
      <Route path="/portal" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PortalDashboard />} />
        <Route path="profile" element={<PortalProfile />} />
        <Route path="orders" element={<PortalOrders />} />
        <Route path="orders/:id" element={<PortalOrderDetail />} />
        <Route path="new-order" element={<PortalNewOrder />} />
        <Route path="end-clients" element={<PortalEndClients />} />
        <Route path="messages" element={<PortalMessages />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
